import React, { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import { toast } from "react-toastify";
import { ConfigContext } from "../../../Api/global/ApiContext";
import { addOfflineStudent } from "../../../Api/apiUtils";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import { Page, PageHeader, Card, Button, Field, TextInput, Select } from "../../../ui";
import "./addOfflineStudent.scss";

const VISIT_WINDOW_OPTIONS = [
  { value: "within_3_days", label: "Within 3 Days" },
  { value: "next_7_days", label: "Next 7 Days" },
  { value: "next_15_days", label: "Next 15 Days" },
  { value: "next_30_days", label: "Next 30 Days" },
] as const;
const HEARD_ABOUT_US_OPTIONS = [
  "Google",
  "Facebook / Instagram",
  "JustDial",
  "Offline Events",
  "Referral by friends/family",
  "Pamphlet / Poster / Standee",
  "Glass facade or advertisement near the Centre Premises",
] as const;

type OfflineCourseOption = {
  name: string;
  offlineCourseId: number;
  interestId: number;
};

const CHENNAI_COURSES: OfflineCourseOption[] = [
  { name: "Tamil Film Music", offlineCourseId: 220, interestId: 122 },
  { name: "Carnatic Classical", offlineCourseId: 221, interestId: 116 },
  { name: "Western Vocal", offlineCourseId: 222, interestId: 118 },
  { name: "Piano/Keyboard", offlineCourseId: 218, interestId: 112 },
  { name: "Guitar", offlineCourseId: 219, interestId: 114 },
];

const WHITEFIELD_COURSES: OfflineCourseOption[] = [
  { name: "Piano/Keyboard", offlineCourseId: 218, interestId: 112 },
  { name: "Guitar", offlineCourseId: 219, interestId: 114 },
  { name: "Carnatic Classical", offlineCourseId: 221, interestId: 116 },
  { name: "Western Vocal", offlineCourseId: 222, interestId: 118 },
  { name: "Tamil Film Music", offlineCourseId: 220, interestId: 122 },
  { name: "Hindi Film Music", offlineCourseId: 251, interestId: 136 },
  { name: "Hindustani Classical", offlineCourseId: 254, interestId: 140 },
  { name: "Telugu Film Music", offlineCourseId: 549, interestId: 186 },
  { name: "Kannada Film Music", offlineCourseId: 551, interestId: 189 },
  
];

const getCoursesForLocation = (location?: { id: string; label: string }): OfflineCourseOption[] => {
  if (!location) return [];
  const name = location.label.toLowerCase();
  const centerId = Number(location.id);

  if (
    name.includes("alwarpet") ||
    name.includes("thoraipakkam") ||
    name.includes("chennai") ||
    centerId === 1
  ) {
    return CHENNAI_COURSES;
  }

  if (
    name.includes("whitefield") ||
    name.includes("wadhwa") ||
    centerId === 6 ||
    centerId === 3
  ) {
    return WHITEFIELD_COURSES;
  }

  return [];
};

const emptyForm = {
  locationId: "",
  visitDate: "",
  studentName: "",
  age: "",
  gender: "",
  email: "",
  phone: "",
  courseId: "",
  heardAboutUs: "",
  apartmentName: "",
};

const splitStudentName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || parts[0] || "",
  };
};

const AddOfflineStudent: React.FC = () => {
  const navigate = useNavigate();
  const config = useContext(ConfigContext);
  const { offlineCentres = [], genders = [], languages = [], ageGroups = [] } = config || {};
  const [form, setForm] = useState(emptyForm);
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locations = useMemo(() => {
    const centres = Array.isArray(offlineCentres) ? offlineCentres : [];
    return centres
      .map((centre: any) => ({
        id: String(centre.centerId ?? centre.centreId ?? ""),
        label: centre.name || centre.centreName || "Unknown location",
        city: centre.city || centre.centreName || centre.name || "",
      }))
      .filter((centre: { id: string }) => centre.id);
  }, [offlineCentres]);

  const selectedLocation = useMemo(
    () => locations.find((location: { id: string }) => location.id === form.locationId),
    [locations, form.locationId]
  );

  const courses = useMemo(
    () => getCoursesForLocation(selectedLocation),
    [selectedLocation]
  );

  const validateEmailFormat = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const resolveGenderId = () => {
    const match = (genders || []).find(
      (gender: any) => String(gender?.name || "").toLowerCase() === form.gender.toLowerCase()
    );
    if (match?.id != null) return Number(match.id);
    return form.gender.toLowerCase() === "female" ? 2 : 1;
  };

  const handleSubmit = async () => {
    if (!form.locationId) {
      toast.error("Location is required");
      return;
    }
    if (!form.visitDate) {
      toast.error("Visit date is required");
      return;
    }
    if (!form.studentName.trim()) {
      toast.error("Student name is required");
      return;
    }
    if (!form.gender) {
      toast.error("Gender is required");
      return;
    }
    if (!form.age) {
      toast.error("Age is required");
      return;
    }
    if (!form.courseId) {
      toast.error("Course is required");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!validateEmailFormat(form.email.trim())) {
      setEmailError("Please enter a valid email address");
      toast.error("Please enter a valid email address");
      return;
    }
    if (!form.phone.trim()) {
      toast.error("Phone number is required");
      return;
    }
    if (!/^\d{10,15}$/.test(form.phone.trim())) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!form.heardAboutUs) {
      toast.error("Please select where you heard about Artium");
      return;
    }
    if (!form.apartmentName.trim()) {
      toast.error("Apartment name is required");
      return;
    }

    const selectedCourse = courses.find(
      (course) => String(course.offlineCourseId) === form.courseId
    );
    const { firstName, lastName } = splitStudentName(form.studentName);
    const preferredLanguageId = Number(languages?.[0]?.id) || 1;

    const payload = {
      firstName,
      lastName,
      emailId: form.email.trim(),
      countryCode: "+91",
      mobileNumber: form.phone.trim(),
      genderId: resolveGenderId(),
      ageGroupId: Number(form.age),
      timezone: "Asia/Kolkata",
      preferredTimezone: "Asia/Kolkata",
      interestId: selectedCourse?.interestId || 1,
      preferredlanguage: preferredLanguageId,
      centerId: Number(form.locationId),
      offlineCourseId: Number(form.courseId),
      addressLocality: selectedLocation?.city || selectedLocation?.label || "",
      heardAboutUs: form.heardAboutUs,
      apartmentName: form.apartmentName.trim(),
    };

    setIsSubmitting(true);
    try {
      await addOfflineStudent(payload);
      trackEvent(MixpanelEvents.OFFLINE_STUDENT_CREATED, {
        email: payload.emailId,
        centerId: payload.centerId,
        offlineCourseId: payload.offlineCourseId,
        courseName: selectedCourse?.name,
        visitDate: form.visitDate,
        ageGroupId: Number(form.age),
        heardAboutUs: payload.heardAboutUs,
      });
      toast.success("Offline student added successfully");
      navigate("/pages/offline-student");
    } catch (error: any) {
      let errorMessage = "Failed to add offline student";
      if (typeof error?.response?.data === "string") {
        errorMessage = error.response.data;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      trackEvent(MixpanelEvents.OFFLINE_STUDENT_CREATE_FAILED, {
        email: payload.emailId,
        errorMessage,
      });
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Offline"
        title="Add Offline Student"
        subtitle="Register a student for an offline centre visit."
        actions={
          <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <Card title="Personal Details">
        <form className="add-offline-student__form" onSubmit={(e) => e.preventDefault()}>
          <Field label="Location" required>
            {(ctl) => (
              <Select
                {...ctl}
                value={form.locationId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    locationId: e.target.value,
                    courseId: "",
                  }))
                }
                options={[
                  { value: "", label: "Select Location" },
                  ...locations.map((location: { id: string; label: string }) => ({
                    value: location.id,
                    label: location.label,
                  })),
                ]}
              />
            )}
          </Field>
          <Field label="Visit Date" required>
            {(ctl) => (
              <Select
                {...ctl}
                value={form.visitDate}
                onChange={(e) => setForm((prev) => ({ ...prev, visitDate: e.target.value }))}
                options={[
                  { value: "", label: "Select" },
                  ...VISIT_WINDOW_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
                ]}
              />
            )}
          </Field>
          <Field label="Student Name" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="text"
                placeholder="Enter student name"
                value={form.studentName}
                onChange={(e) => setForm((prev) => ({ ...prev, studentName: e.target.value }))}
              />
            )}
          </Field>
          <Field label="Gender" required>
            {(ctl) => (
              <Select
                {...ctl}
                value={form.gender}
                onChange={(e) => setForm((prev) => ({ ...prev, gender: e.target.value }))}
                options={[
                  { value: "", label: "Select Gender" },
                  ...(genders || []).map((gender: any) => ({
                    value: String(gender.name),
                    label: String(gender.name),
                  })),
                ]}
              />
            )}
          </Field>
          <Field label="Age" required>
            {(ctl) => (
              <Select
                {...ctl}
                value={form.age}
                onChange={(e) => setForm((prev) => ({ ...prev, age: e.target.value }))}
                options={[
                  { value: "", label: "Select Age" },
                  ...(ageGroups || []).map((age: any) => ({
                    value: String(age.id),
                    label: String(age.name),
                  })),
                ]}
              />
            )}
          </Field>
          <Field label="Course" required>
            {(ctl) => (
              <Select
                {...ctl}
                value={form.courseId}
                onChange={(e) => setForm((prev) => ({ ...prev, courseId: e.target.value }))}
                disabled={!form.locationId}
                options={[
                  { value: "", label: form.locationId ? "Select Course" : "Select a location first" },
                  ...courses.map((course) => ({
                    value: String(course.offlineCourseId),
                    label: course.name,
                  })),
                ]}
              />
            )}
          </Field>
          <Field label="Email" required error={emailError || undefined}>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="email"
                placeholder="Enter email"
                value={form.email}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((prev) => ({ ...prev, email: value }));
                  if (value && !validateEmailFormat(value)) {
                    setEmailError("Please enter a valid email address");
                  } else {
                    setEmailError("");
                  }
                }}
              />
            )}
          </Field>
          <Field label="Phone Number" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="tel"
                maxLength={10}
                placeholder="Enter phone number"
                value={form.phone}
                inputMode="numeric"
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    phone: e.target.value.replace(/\D/g, "").slice(0, 15),
                  }))
                }
              />
            )}
          </Field>
          <Field label="Where did you hear about Artium?" required>
            {(ctl) => (
              <Select
                {...ctl}
                value={form.heardAboutUs}
                onChange={(e) => setForm((prev) => ({ ...prev, heardAboutUs: e.target.value }))}
                options={[
                  { value: "", label: "Select" },
                  ...HEARD_ABOUT_US_OPTIONS.map((option) => ({ value: option, label: option })),
                ]}
              />
            )}
          </Field>
          <Field label="Apartment name" required>
            {(ctl) => (
              <TextInput
                {...ctl}
                type="text"
                placeholder="Enter apartment name"
                value={form.apartmentName}
                onChange={(e) => setForm((prev) => ({ ...prev, apartmentName: e.target.value }))}
              />
            )}
          </Field>
        </form>

        <div className="add-offline-student__actions">
          <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </Card>
    </Page>
  );
};

export default AddOfflineStudent;
